import {SearchResultState} from './searchresultstate.js';

/**
 * A item that would appear in search results.
 * @typedef {Object} SearchResultItem
 * @property {Number} id - The id of the item.
 * @property {String} type - The type of the item, eg: "Product".
 * @property {String} name - The name of the item.
 * @property {String} description - A short description of the item.
 * @property {?String} image - The url to the image of the item or null.
 * @property {Number} relevanceScore - A number indicating the relevance of the 
 *     item to the query
 */

/**
 * An object describing the result of a predictive search.
 * @typedef {Object} PredictedSearchResult
 * @property {String} user - The shop username.
 * @property {String} query - The query that the results are for.
 * @property {Array.<SearchResultItem>} results - The search results.
 */

/**
 * The application state.
 * @typedef {Object} ApplicationState
 * @property {Boolean} isSearchPage - Is the current page for showing search results.
 * @property {String} user - The shop username.
 * @property {Object} inputs - The state for search query inputs.
 * @property {Array.<HTMLInputElement>} inputs.elements - The input elements on 
 *     the page.
 * @property {?Number} inputs.activeElement - The index of the active input 
 *     element or null.
 * @property {String} activeInputQuery - The query text of the active input.
 * @property {Object} suggestions - The state for search suggestions.
 * @property {Array.<String>} suggestions.queries - The suggested queries.
 * @property {?Number} suggestions.activeQuery - The index of the active 
 *     suggested query.
 * @property {Array.<SearchResultItem>} suggestions.items - The predicated 
 *     search results.
 * @property {Array.<PredictedSearchResult>} suggestions.history - A cache of 
 *     predictive search results.
 * @property {Object} search - The state of the normal search.
 * @property {SearchResultState} search.state - The current condition of the search.
 * @property {String} search.query - The search query.
 * @property {Array.<SearchResultItem>} search.items - The search results.
 * @property {Number} search.page - The page of results being displayed.
 * @property {Number} search.totalPages - The total number of pages of results.
 * @property {Number} serach.totalItems - The total number of results.
 * @example
 * {
 *     isSearchPage: Boolean,
 *     user: String,
 *     inputs: {
 *         elements: Array<HTMLInputElement>,
 *         activeElement: Null|Number
 *     },
 *     activeInputQuery: String,
 *     suggestions: {
 *         queries: Array<string>,
 *         activeQuery: Null|Number,
 *         items: Array<SearchResultItem>,
 *         history: Array<PredictedSearchResult>
 *     },
 *     search: {
 *         state: String,
 *         query: String,
 *         items: Array<SearchResultItem>,
 *         page: Number,
 *         totalPages: Number,
 *         totalItems: Number
 *     }
 * }
 */

export function createState(values){
	values = values || {};

	var user = values.user || '';
	var inputs = values.inputs || [];
	var isSearchPage = values.isSearchPage || false;
	var searchQuery = values.searchQuery || '';
	var searchCategories = values.searchCategories || [];
	var searchAttributes = values.searchAttributes || {};
	var token = values.token || '';
	var showCategoryFilter = values.showCategoryFilter || false;
	var topLevelCategories = values.topLevelCategories || [];
	var showAttributeFilter = values.showAttributeFilter || false;
	var attributes = values.attributes || {};
	var showPriceFilter = values.showPriceFilter || false;
	var minPrice = values.priceRange.min || 0;
	var maxPrice = values.priceRange.max || Number.MAX_SAFE_INTEGER;
	var {exponent=2, symbol='£', format='SYMBOL-PRICE'} = values.currency || {};
	var showEditButtons = values.showEditButtons || false;

	var attributeNames = values.attributeNames || {}; // field added 2024-04-18

	var showTDPrices = (values.showTDPrices != undefined)
		? values.showTDPrices
		: true;

	var isTDUser = (values.isTDUser != undefined)
		? values.isTDUser
		: false;

	var showPrices = showTDPrices || isTDUser;

	// The equality (==) operator is intentional used here instead of identity 
	// (===) operator, to match both undefined and null.
	var searchMinPrice = (values.searchMinPrice == undefined)
		? null 
		: Math.max(values.priceRange, minPrice);

	var searchMaxPrice = (values.searchMaxPrice == undefined)
		? null 
		: Math.min(values.priceRange, maxPrice);

	return {
		isSearchPage: isSearchPage,
		token: token,
		user: user,
		inputs: {
			elements: inputs,
			activeElement: null
		},
		activeInputQuery: '',
		suggestions: {
			queries: [],
			activeQuery: null,
			items: [],
			history: []
		},
		recentQueries: {
			items: [],
			activeItem: null
		},
		search: {
			state: SearchResultState.UNLOADED,
			query: searchQuery,
			similarQueries: [],
			categories: searchCategories,
			attributes: searchAttributes,
			price: {
				min: searchMinPrice, 
				max: searchMaxPrice
			},
			items: [],
			similarItems: [],
			page: 0,
			totalPages: 0,
			totalItems: 0,
			loadingMore: false
		},
		ui: {
			filtersVisible: false,
			gridView: true,
			listView: false,
		},
		showEditButtons: showEditButtons,
		showCategoryFilter: showCategoryFilter,
		topLevelCategories: topLevelCategories,
		showAttributeFilter: showAttributeFilter,
		showPrices,
		attributes: attributes,
		attributeNames,
		collapsedAttributes: [],
		facets: {},
		filteredAttributes: {},
		showPriceFilter: showPriceFilter,
		priceRange: {
			min: minPrice, 
			max: maxPrice
		},
		currency: {
			exponent,
			symbol,
			format
		}
	}
}